// Context object + type + hook for GenerationContext, split out of the .tsx so the
// component file only exports the GenerationProvider component
// (react-refresh/only-export-components).

import { createContext, useContext } from 'react';
import type {
  GenerationJob,
  GenerationTabMode,
  JobError,
  StartJobInput,
} from '../types/generationJob';

export interface GenerationContextValue {
  startJob: <TMeta = unknown>(input: StartJobInput<TMeta>) => GenerationJob<unknown, TMeta>;
  updateJob: (id: string, patch: Partial<GenerationJob>) => void;
  completeJob: (id: string, result: unknown, timing?: GenerationJob['timing']) => void;
  failJob: (id: string, error: JobError, status?: 'failed' | 'expired' | 'cancelled') => void;
  cancelJob: (id: string) => void;
  clearJob: (id: string) => void;
  /**
   * Re-read a finished job and store the result the backend reports now. The
   * backend re-signs a result's expiring media links on every read, so this is
   * how a result on screen gets live links again. Resolves with the fresh
   * result; rejects when the job can no longer be read.
   */
  refreshResult: (id: string) => Promise<unknown>;
  clearResults: (tab: GenerationTabMode) => void;
  getJob: (id: string) => GenerationJob | undefined;
  getActiveByTab: (tab: GenerationTabMode) => GenerationJob[];
  getLatestActiveByTab: (tab: GenerationTabMode) => GenerationJob | undefined;
  getLatestResultByTab: (tab: GenerationTabMode) => GenerationJob | undefined;
  getResultsByTab: (tab: GenerationTabMode) => GenerationJob[];
  /** Aggregated (tab -> active count). Referentially stable per state change. */
  activeCountsByTab: ReadonlyMap<GenerationTabMode, number>;
  /** This browser tab's owner id; only the owner runs pollers for its jobs. */
  ownerTabId: string;
}

export const GenerationCtx = createContext<GenerationContextValue | null>(null);

export function useGeneration(): GenerationContextValue {
  const ctx = useContext(GenerationCtx);
  if (!ctx) {
    throw new Error('useGeneration must be used within a GenerationProvider');
  }
  return ctx;
}
